import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useFetchAllBlogPosts } from '@/useQuery/queries/blogs';
import BlogMeta from '@/components/seo/meta/BlogMeta';
import Container from '@/components/Container';
import BlogPageCard from '@/components/blog/BlogPageCard';
import { PrimaryButton, PrimaryButtonLink } from '@/components/common/PrimaryButton';
import { ImagesBlogBlogTopicsWebp } from '@/assets/images_blog.assets';
import Image from 'next/image';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { BlogEnum } from '@/types/translations/BlogEnum';
import BlogHeader from '@/components/blog/BlogHeader';
import BlogLatestBlogPosts from '@/components/blog/BlogLatestBlogPosts';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';

const Blog = () => {
  const { translate } = useTypedTranslation<BlogEnum>('blog');
  const [showHighlights, setShowHighlights] = useState(false);
  const router = useRouter();
  const blogsQuery = useFetchAllBlogPosts(router.locale as string);
  const blogs = blogsQuery.data || [];

  return (
    <>
      <BlogMeta />
      <div className="tw-w-full tw-bg-blogBg tw-bg-cover tw-bg-center">
        <div className="tw-py-40 max-nine:tw-py-20 max-sm:tw-bg-main-shades-100 max-sm:tw-bg-opacity-60">
          <Container>
            <div>
              <div className="tw-w-2/5 max-sm:tw-w-full max-sm:tw-text-center">
                <div className="tw-w-fit">
                  <h1 className="tw-font-prata tw-text-6xl tw-font-normal tw-text-text max-nine:tw-text-2xl">
                    {translate(BlogEnum.blog)}
                  </h1>
                  <hr
                    className="tw-m-0 tw-my-4 tw-h-1 tw-w-5/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100 max-sm:tw-w-[150%]"
                    aria-hidden="true"
                  />{' '}
                </div>
                <p className="tw-font-plusJakartaSans tw-text-xl tw-font-medium tw-text-text max-nine:!tw-text-base max-sm:tw-w-[80%] max-sm:tw-text-left">
                  {translate(BlogEnum.blog_subtitle)}
                </p>
              </div>
            </div>
          </Container>
        </div>
      </div>
      <BlogHeader />
      <BlogLatestBlogPosts blogs={blogs} blogsLoading={blogsQuery.isLoading} />

      <div className="tw-mb-[-32px] tw-mt-24 tw-w-full tw-bg-blogSignUp tw-bg-cover tw-bg-center">
        <div className="tw-h-full tw-w-full tw-bg-main tw-bg-opacity-90">
          <Container className="tw-px-16 tw-py-20">
            <div className="tw-flex tw-items-center tw-justify-between max-sm:tw-flex-col max-sm:tw-items-center max-sm:tw-text-center">
              <div>
                <h2 className="tw-font-prata tw-text-6xl tw-font-normal tw-text-white max-nine:tw-text-xl">
                  {translate(BlogEnum.you_dont_have_an_account_yet)}
                </h2>
                <p className="tw-mt-4 tw-font-plusJakartaSans tw-text-3xl tw-font-semibold tw-text-white max-nine:!tw-text-base">
                  {translate(BlogEnum.create_it_right_now)}
                </p>
              </div>
              <PrimaryButtonLink
                href="/auth/sign"
                white={true}
                className="tw-px-16 tw-text-xl tw-font-semibold"
              >
                {translate(BlogEnum.sign_up_now)}
              </PrimaryButtonLink>
            </div>
          </Container>
        </div>
      </div>
      <Container className="tw-mb-16 tw-hidden">
        <div className="tw-w-fit tw-pb-9">
          <h2 className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-text-text max-nine:tw-text-sm">
            {translate(BlogEnum.popular_topics_among_users)}
          </h2>
          <hr
            className="tw-m-0 tw-mt-2 tw-h-1 tw-w-4/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100"
            aria-hidden="true"
          />{' '}
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-stretch tw-justify-between tw-gap-4 child:tw-relative child:tw-flex child:tw-basis-[calc(33.33%-1rem)] child:tw-items-center child:tw-justify-center child:tw-rounded-md child:tw-py-14 max-md:child:tw-w-[calc(50%-1rem)] max-md:child:tw-basis-[calc(50%-1rem)] max-sm:child:tw-basis-full">
          <div>
            <div
              aria-hidden={true}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-20 tw-h-full tw-w-full tw-rounded-xl tw-bg-black tw-bg-opacity-60"
            ></div>
            <Image
              src={ImagesBlogBlogTopicsWebp}
              alt="topic1"
              width={422}
              height={196}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-rounded-xl tw-object-cover"
            />
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Top 10 Whisky
              </h3>
              <b className="tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-white max-nine:tw-text-sm">
                in Germany
              </b>
            </div>
          </div>
          <div>
            <div
              aria-hidden={true}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-20 tw-h-full tw-w-full tw-rounded-xl tw-bg-black tw-bg-opacity-60"
            ></div>
            <Image
              src={ImagesBlogBlogTopicsWebp}
              alt="topic1"
              width={422}
              height={196}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-rounded-xl tw-object-cover"
            />
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Whisky Guides
              </h3>
            </div>
          </div>
          <div>
            <div
              aria-hidden={true}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-20 tw-h-full tw-w-full tw-rounded-xl tw-bg-black tw-bg-opacity-60"
            ></div>
            <Image
              src={ImagesBlogBlogTopicsWebp}
              alt="topic1"
              width={422}
              height={196}
              className="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-rounded-xl tw-object-cover"
            />
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Trends
              </h3>
            </div>
          </div>

          <div className="tw-bg-main">
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Top 10 Whisky
              </h3>
              <b className="tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-white max-nine:tw-text-sm">
                in Germany
              </b>
            </div>
          </div>

          <div className="tw-bg-main">
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Whisky Guides
              </h3>
            </div>
          </div>

          <div className="tw-bg-main">
            <div className="tw-relative tw-z-30 tw-flex tw-flex-col tw-items-center">
              <h3 className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:!tw-text-lg">
                Trends
              </h3>
            </div>
          </div>
        </div>
      </Container>
      <Container className={`tw-hidden`}>
        <div className="tw-w-fit tw-pb-9">
          <h2 className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-text-text max-nine:tw-text-sm">
            {translate(BlogEnum.highlights_of_the_month)}
          </h2>
          <hr
            className="tw-m-0 tw-mt-2 tw-h-1 tw-w-4/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100"
            aria-hidden="true"
          />{' '}
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-4 child:tw-w-[calc(33.33%-1rem)] child:tw-basis-[calc(33.33%-1rem)] max-md:child:tw-w-[calc(50%-1rem)] max-md:child:tw-basis-[calc(50%-1rem)] max-sm:child:tw-basis-full">
          {blogsQuery.isLoading === true
            ? Array.from({ length: 6 }).map((_, index) => <BlogPageCard key={index} blog={null} />)
            : blogs.length <= 0
              ? null
              : [...blogs]
                  .slice(1, showHighlights ? blogs.length : 7)
                  .map(blog => <BlogPageCard key={blog.id} blog={blog} />)}
        </div>
        <div className="tw-mt-12 tw-flex tw-items-center tw-justify-center">
          <PrimaryButton
            type="button"
            outlined={true}
            className="tw-px-16 tw-text-lg tw-font-bold"
            onClick={() => setShowHighlights(prev => !prev)}
          >
            {showHighlights ? translate(BlogEnum.less_articles) : translate(BlogEnum.all_articles)}
          </PrimaryButton>
        </div>
      </Container>
    </>
  );
};

export const getStaticProps = getBasicStaticSideProps(['common', 'index', 'blog', 'meta']);

export default Blog;
